import { useState } from 'react';
import { toast } from 'sonner';
import { useGmbAlerts, useAcknowledgeGmbAlert, useRunGmbAlertChecks, useGmbGovernanceOverview } from '../../hooks/useGmbGovernance';
import { resolveSectionId } from '../../utils/navigation';
import GMBEmptyState from './shared/GMBEmptyState';
import { GBMContentWrapper } from './shared/GMBLoadingState';

interface AlertsAndPermissionsProps {
  onNavigate?: (section: string) => void;
}

const severityConfig: Record<string, { badgeBg: string; badgeText: string; icon: string; border: string; accent: string }> = {
  critical: {
    badgeBg: 'bg-red-100 dark:bg-red-900/30',
    badgeText: 'text-red-700 dark:text-red-300',
    icon: 'ri-error-warning-line',
    border: 'border-red-200 dark:border-red-800',
    accent: 'text-red-600',
  },
  warning: {
    badgeBg: 'bg-amber-100 dark:bg-amber-900/30',
    badgeText: 'text-amber-700 dark:text-amber-300',
    icon: 'ri-alert-line',
    border: 'border-amber-200 dark:border-amber-800',
    accent: 'text-amber-600',
  },
  informational: {
    badgeBg: 'bg-sky-100 dark:bg-sky-900/30',
    badgeText: 'text-sky-700 dark:text-sky-300',
    icon: 'ri-information-line',
    border: 'border-sky-200 dark:border-sky-800',
    accent: 'text-sky-600',
  },
};

const alertTypeIcons: Record<string, string> = {
  review_pending: 'ri-star-line',
  rating_drop: 'ri-arrow-down-line',
  posting_inactive: 'ri-calendar-close-line',
  google_edit: 'ri-edit-circle-line',
  missing_info: 'ri-time-line',
  duplicate_listing: 'ri-file-copy-line',
  health_score_drop: 'ri-percent-line',
  auth_issue: 'ri-lock-unlock-line',
  new_review: 'ri-star-smile-line',
  profile_score: 'ri-profile-line',
};

export default function AlertsAndPermissions({ onNavigate }: AlertsAndPermissionsProps) {
  const [severityFilter, setSeverityFilter] = useState('all');
  const [isRunning, setIsRunning] = useState(false);
  const { data: alertsData, isLoading, error, refetch } = useGmbAlerts(severityFilter);
  const acknowledge = useAcknowledgeGmbAlert();
  const runChecks = useRunGmbAlertChecks();
  const { data: overviewData } = useGmbGovernanceOverview();

  const MIN_RUN_DURATION_MS = 800;

  const handleRunChecks = async () => {
    setIsRunning(true);
    const startedAt = Date.now();
    try {
      const res = await runChecks.mutateAsync();
      const count = (res as any)?.new_alerts_count ?? 0;
      if (count > 0) {
        toast.info(`${count} new alert${count === 1 ? '' : 's'} generated`);
      } else {
        toast.success('Alert checks complete — no new alerts');
      }
    } catch (err) {
      toast.error('Failed to run alert checks');
    } finally {
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(0, MIN_RUN_DURATION_MS - elapsed);
      setTimeout(() => setIsRunning(false), remaining);
    }
  };

  const data = alertsData?.data || {};
  const alertRows = data.alerts || [];
  const overview = overviewData?.data || {};
  const isFullAccess = overview.google_account_status === 'full_access';
  const isVerified = overview.verification_status === 'verified' || overview.verification_status === 'active';
  const roles = [
    { role: 'Admin', desc: 'Full control — manage the profile, permissions and team', isCurrent: true },
    { role: 'Manager', desc: 'Manage day-to-day profile activities', isCurrent: false },
    { role: 'Viewer', desc: 'View the profile and activity only', isCurrent: false },
  ];
  const counts = [
    { label: 'Total Alerts', value: data.total ?? 0, icon: 'ri-notification-line', color: 'text-gray-600 bg-gray-100' },
    { label: 'Critical', value: data.critical_count ?? 0, icon: 'ri-error-warning-line', color: 'text-red-600 bg-red-50' },
    { label: 'Warning', value: data.warning_count ?? 0, icon: 'ri-alert-line', color: 'text-amber-600 bg-amber-50' },
    { label: 'Informational', value: data.informational_count ?? 0, icon: 'ri-information-line', color: 'text-sky-600 bg-sky-50' },
  ];

  return (
    <GBMContentWrapper error={error} onRetry={() => refetch()} loading={isLoading}>
      <div className="space-y-5">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative w-3 h-3">
              <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-60" />
              <div className="relative w-3 h-3 bg-green-500 rounded-full" />
            </div>
            <div>
              <div className="text-sm font-medium text-gray-800 dark:text-gray-200">Monitored Automatically</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Checks run every 15 minutes. You can also trigger a check manually any time.</div>
            </div>
          </div>
          <button
            onClick={() => handleRunChecks()}
            disabled={isRunning}
            className="flex items-center gap-1.5 px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            <i className={`${isRunning ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'}`} />
            {isRunning ? 'Running…' : 'Run Checks'}
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {counts.map((s) => (
            <div key={s.label} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-1.5">
                <div className={`w-7 h-7 ${s.color} rounded-md flex items-center justify-center`}>
                  <i className={`${s.icon} text-xs`} />
                </div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{s.label}</span>
              </div>
              <div className="text-xl font-bold text-gray-900 dark:text-white">{s.value}</div>
            </div>
          ))}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-lock-2-line text-teal-600 text-sm"></i>
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Access & Permissions</h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div className="flex items-center justify-between gap-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Google Account</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {isFullAccess ? 'Full Access' : 'Unlinked'}
                </div>
                <div className="text-[11px] text-gray-400">{isFullAccess ? 'Connected to Google' : 'Not connected'}</div>
              </div>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${isFullAccess ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
                {isFullAccess ? 'Connected' : 'Action needed'}
              </span>
            </div>

            <div className="flex items-center justify-between gap-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Verification</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {isVerified ? 'Verified' : 'Unverified'}
                </div>
                <div className="text-[11px] text-gray-400">{isVerified ? 'Listing confirmed by Google' : 'Complete verification to unlock full features'}</div>
              </div>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${isVerified ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'}`}>
                {isVerified ? 'Verified' : 'Pending'}
              </span>
            </div>
          </div>

          <div className="mb-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Team roles</div>
            <div className="space-y-2">
              {roles.map((r) => (
                <div key={r.role} className="flex items-center justify-between gap-2 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 min-w-0">
                    <i className="ri-user-line text-gray-400 text-sm"></i>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-800 dark:text-gray-200 flex items-center gap-2">
                        {r.role}
                        {r.isCurrent && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300">You</span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-400 truncate">{r.desc}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-[11px] text-gray-400">Roles are listed for future team support. The platform currently operates as a single user (Admin).</p>
            <button
              onClick={() => onNavigate?.('live-preview')}
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              View Live Preview
              <i className="ri-arrow-right-line text-xs" />
            </button>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Active Alerts</h3>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-800"
            >
              <option value="all">All Severities</option>
              <option value="critical">Critical</option>
              <option value="warning">Warning</option>
              <option value="informational">Informational</option>
            </select>
          </div>

          <div className="space-y-3">
            {alertRows.map((alert: any) => {
              const c = severityConfig[alert.severity] || severityConfig.informational;
              const typeIcon = alertTypeIcons[alert.alert_type] || 'ri-notification-line';
              return (
                <div key={alert.id} className={`bg-white dark:bg-gray-800 rounded-lg border ${c.border} p-4 shadow-sm transition-all hover:shadow-md ${alert.is_acknowledged ? 'opacity-60' : ''}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-md ${c.badgeBg} flex items-center justify-center flex-shrink-0`}>
                      <i className={`${typeIcon} ${c.accent} text-sm`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${c.badgeBg} ${c.badgeText}`}>{alert.severity}</span>
                        <span className="text-[10px] text-gray-400 uppercase tracking-wide">{alert.alert_type_label || alert.alert_type.replace(/[-_]/g, ' ')}</span>
                      </div>
                      <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{alert.title}</h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">{alert.message}</p>
                      <div className="flex items-center gap-2 mt-3">
                        {alert.cta_label && (
                          <button
                            onClick={() => onNavigate?.(resolveSectionId(alert.cta_action))}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
                          >
                            {alert.cta_label}
                            <i className="ri-arrow-right-line text-xs" />
                          </button>
                        )}
                        {!alert.is_acknowledged && (
                          <button
                            onClick={() => acknowledge.mutate(alert.id)}
                            disabled={acknowledge.isPending}
                            className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                          >
                            <i className="ri-check-line" />
                            Acknowledge
                          </button>
                        )}
                        <span className="text-[10px] text-gray-400 ml-auto">{alert.created_at_label}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {alertRows.length === 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center shadow-sm">
                <div className="w-12 h-12 bg-green-50 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-check-double-line text-green-500 text-xl" />
                </div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">All caught up!</p>
                <p className="text-xs text-gray-400 mt-1">No active alerts matching your filter.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </GBMContentWrapper>
  );
}